/* eslint-disable react-hooks/rules-of-hooks */
import { observer } from "mobx-react";
import {
  Button,
  Checkbox,
  ColorInput,
  Flex,
  Slider,
  Text,
  createStyles,
} from "@mantine/core";
import { useStore } from "../../../../hooks/use-store";
import { useRequest } from "ahooks";
import { NOTIFICATION_SERVICE } from "../../../../components/notifications/NotificationService";
import ImageObserved from "../Image";
import { PromptField } from "./PromptField";
import { MODE_2 } from "../../../../stores/data/space-store/space-store";
import { PipelineParameters } from "../Pipeline";
import { priceMap } from "../Router";
import BottomButton from "./BottomButton";
import { SidebarTrigger } from "./Sidebar";
import { Widget } from "@uploadcare/react-widget";

const useStyles = createStyles((theme) => ({
  uploadContainer: {
    backgroundColor: '#4f4f4f',
    borderRadius: '4px',
    height: '288px',
    overflow: 'hidden',
  },
  widgetRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    '> div': {
      textAlign: 'center',
    }
  }
}));

const Element = (model: {
  buy: (params: PipelineParameters[]) => Promise<void>;
  loading: boolean
}) => {

  const {
    dataStore: { spaceStore, appStore },
  } = useStore();

  const { classes } = useStyles();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { loading: runAsyncConvertLoading, runAsync: runAsyncConvert } = useRequest(
    async (model: { url: string, removeBackground?: boolean }) => appStore.convertImage(model),
    {
      manual: true,
      onSuccess: (res) => {
        if (res.error) {
          NOTIFICATION_SERVICE.errorNotification(
            "Sorry, service is unavailable due to high demand. Please try later"
          );
        } else {
          return res.url
        }
      },
    }
  );

  const { loading: runAsyncVisionLoading, runAsync: runAsyncVision } = useRequest(
    async (model: { url: string }) => appStore.vision({
      url: model.url,
    }),
    {
      manual: true,
      onSuccess: (res) => {
        if (res.error) {
          NOTIFICATION_SERVICE.errorNotification(
            "Sorry, service is unavailable due to high demand. Please try later"
          );
        } else {
          return res.url
        }
      },
    }
  );

  return (
    <Flex direction={'column'} w="100%" pos='relative' gap={12} mt={20} mb={60}>
      <ImageObserved
        src={spaceStore.selectedModel?.mainImage}
        width="100%"
        height="auto"
        imgStyle={{ borderRadius: '6px', overflow: 'hidden' }}
      >
      </ImageObserved>
      <Flex
        style={{
          position: 'absolute',
          right: '-320px',
          top: '300px',
          width: '300px',
          zIndex: 1000,
        }}
        id="sidebar"
      >
      </Flex>
      <PromptField
        credits={spaceStore.secondLoraName ? spaceStore.carPerspectives.length * priceMap.VARIATIONS : priceMap.VARIATIONS}
        onClick={async () => {
          if (spaceStore.secondLoraName) {
            const prompt = spaceStore.secondLoraName ? spaceStore.prompt : (spaceStore.prompt + `, 'photograph location in style of MAGAI`)

            for (const perspective of spaceStore.carPerspectives) {
              const pipeline: PipelineParameters = {
                provider: 'replicate',
                type: 'customModelV1',
                modelName: spaceStore.selectedModel!.name,
                modelVersion: spaceStore.selectedModel!.modelVersion!,
                prompt: prompt,
                aspectRation: spaceStore.ratio,
                loraScale: spaceStore.locationScale.toString(),
                secondLoraScale: spaceStore.secondLoraScale.toString(),
                secondLoraName: spaceStore.secondLoraName,
                credits: priceMap.VARIATIONS,
                replicateVersion: spaceStore.selectedModel!.modelVersion!,
                steps: spaceStore.steps,
                cfg: spaceStore.cfg,
                srcset: [],
                color: spaceStore.carColor,
                carPerspective: perspective,
                motionBlur: ''
              }
              await model.buy([pipeline])
            }
          }
          else {
            const prompt = (spaceStore.prompt + `, photograph of location in style of MAGAI`)

            const pipeline: PipelineParameters = {
              provider: 'replicate',
              type: 'customModelV1',
              modelName: spaceStore.selectedModel!.name,
              modelVersion: spaceStore.selectedModel!.modelVersion!,
              prompt: prompt,
              aspectRation: spaceStore.ratio,
              loraScale: spaceStore.locationScale.toString(),
              secondLoraScale: spaceStore.secondLoraScale.toString(),
              secondLoraName: spaceStore.secondLoraName,
              credits: priceMap.VARIATIONS,
              replicateVersion: spaceStore.selectedModel!.modelVersion!,
              steps: spaceStore.steps,
              cfg: spaceStore.cfg,
              srcset: [],
              color: spaceStore.carColor,
              carPerspective: '',
              motionBlur: ''
            }

            await model.buy([pipeline]);
          }
        }}
        value={spaceStore.prompt}
        onChangeEnd={(value) => {
          spaceStore.setPrompt(value)
        }}
        loading={model.loading}
        placeholder="Describe an image and click generate"
      >
        <Flex gap={8} p={10} wrap={'wrap'} pr={'100px'} w={'100%'}>
          {
            spaceStore.mode2 === MODE_2.CAR_LORA ? <>
              <Button
                size={'xs'}
                variant="filled"
                color="orange"
                onClick={() => {
                  spaceStore.setSecondLora(null)
                }}
              >
                {spaceStore.secondLoraDisplayName}
              </Button>

              <SidebarTrigger
                target={
                  <BottomButton
                    loading={false}
                    dataIntercomTarget={
                      'CarSettingsButton'
                    }
                    label="Car settings"
                    icon={<svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.3089 11.3122V13.3122C15.3089 14.2322 14.5622 14.9788 13.6422 14.9788H8.8822C8.28887 14.9788 7.99553 14.2655 8.40887 13.8455L12.3222 9.84551C12.4489 9.71884 12.6222 9.64551 12.7955 9.64551H13.6422C14.5622 9.64551 15.3089 10.3922 15.3089 11.3122Z" fill="#BDBDBD" />
                      <path d="M12.8887 7.8395L11.082 9.64617L9.442 11.2795C9.022 11.6995 8.302 11.4062 8.302 10.8128C8.302 8.67284 8.302 5.15284 8.302 5.15284C8.302 4.97284 8.37534 4.80617 8.49534 4.67951L9.10867 4.06617C9.762 3.41284 10.8153 3.41284 11.4687 4.06617L12.882 5.47951C13.542 6.13284 13.542 7.18617 12.8887 7.8395Z" fill="#BDBDBD" />
                      <path d="M5.64201 1.64551L3.64201 1.64551C2.64201 1.64551 1.97534 2.31217 1.97534 3.31217L1.97534 12.3122C1.97534 12.4922 1.99534 12.6722 2.02868 12.8455C2.04868 12.9322 2.06868 13.0188 2.09534 13.1055C2.12868 13.2055 2.16201 13.3055 2.20201 13.3988C2.20868 13.4055 2.20868 13.4122 2.20868 13.4122C2.21534 13.4122 2.21534 13.4122 2.20868 13.4188C2.30201 13.6055 2.40868 13.7855 2.53534 13.9522C2.60868 14.0388 2.68201 14.1188 2.75534 14.1988C2.82868 14.2788 2.90868 14.3455 2.99534 14.4122L3.00201 14.4188C3.16868 14.5455 3.34868 14.6522 3.53534 14.7455C3.54201 14.7388 3.54201 14.7388 3.54201 14.7455C3.64201 14.7922 3.74201 14.8255 3.84868 14.8588C3.93534 14.8855 4.02201 14.9055 4.10867 14.9255C4.28201 14.9588 4.46201 14.9788 4.64201 14.9788C4.91534 14.9788 5.19534 14.9388 5.45534 14.8522C5.52867 14.8255 5.60201 14.7988 5.67534 14.7655C5.90867 14.6722 6.13534 14.5388 6.33534 14.3655C6.39534 14.3188 6.46201 14.2588 6.52201 14.1988L6.54867 14.1722C7.01534 13.6922 7.30867 13.0322 7.30867 12.3122L7.30867 3.31217C7.30867 2.31217 6.64201 1.64551 5.64201 1.64551ZM4.64201 13.3122C4.08868 13.3122 3.64201 12.8655 3.64201 12.3122C3.64201 11.7588 4.08868 11.3122 4.64201 11.3122C5.19534 11.3122 5.64201 11.7588 5.64201 12.3122C5.64201 12.8655 5.19534 13.3122 4.64201 13.3122Z" fill="#BDBDBD" />
                    </svg>}
                  />
                }
                body={
                  <Flex direction={'column'} gap={8} p={10} w={'280px'} justify={'center'}>
                    <ColorInput
                      label="Color of the car"
                      size="sm"
                      value={spaceStore.carColor}
                      // withPicker={false}
                      styles={{
                        rightSection: {
                          display: 'none',
                        },
                      
                        root: {
                          // zIndex: 1,
                        },
                        input: {
                        }
                      }}
                      onChangeEnd={(e: any) => {
                        console.log(e)
                        spaceStore.setCarColor(e!)
                      }}
                      format="hex"
                    />
                    <Text
                      fz={14}
                      mt={12}
                      size="xs">
                      Select the car perspectives
                    </Text>
                    {
                      [
                        { value: 'Front View', label: 'Front View' },
                        { value: 'Rear View', label: 'Rear View' },
                        { value: 'Side View (Left)', label: 'Side View (Left)' },
                        { value: 'Side View (Right)', label: 'Side View (Right)' },
                        { value: 'Front 3/4 View (Left)', label: 'Front 3/4 View (Left)' },
                        { value: 'Front 3/4 View (Right)', label: 'Front 3/4 View (Right)' },
                        { value: 'Rear 3/4 View (Left)', label: 'Rear 3/4 View (Left)' },
                        { value: 'Rear 3/4 View (Right)', label: 'Rear 3/4 View (Right)' },
                      ].map((side) => (
                        <Checkbox
                          mt={2}
                          mb={2}
                          color="orange"
                          size={'xs'}
                          styles={{
                            input: {
                              borderRadius: 2,
                              borderColor: 'orange',
                              backgroundColor: 'transparent',
                            }
                          }}
                          label={side.label}
                          checked={spaceStore.carPerspectives.includes(side.value)}
                          onChange={(e) => {
                            if (spaceStore.carPerspectives.includes(side.value)) {
                              spaceStore.setCarPerspectives(spaceStore.carPerspectives.filter(itm => itm !== side.value))
                            } else {
                              spaceStore.setCarPerspectives([...spaceStore.carPerspectives, side.value])
                            }
                          }}
                        />
                      ))
                    }
                  </Flex>
                }
              />
            </> : null
          }

          <SidebarTrigger
            target={
              <BottomButton
              dataIntercomTarget={
                'AddInfluenceImageButton'
              }
                loading={false}
                label={'Add influence image'}
                icon={
                  <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.951 0.666504H12.991C12.411 0.666504 11.9844 0.906504 11.791 1.33317C11.6844 1.5265 11.6377 1.75317 11.6377 2.01984V3.97984C11.6377 4.8265 12.1444 5.33317 12.991 5.33317H14.951C15.2177 5.33317 15.4444 5.2865 15.6377 5.17984C16.0644 4.9865 16.3044 4.55984 16.3044 3.97984V2.01984C16.3044 1.17317 15.7977 0.666504 14.951 0.666504ZM15.5777 3.2865C15.511 3.35317 15.411 3.39984 15.3044 3.4065H14.3644V3.7465L14.371 4.33317C14.3644 4.4465 14.3244 4.53984 14.2444 4.61984C14.1777 4.6865 14.0777 4.73317 13.971 4.73317C13.751 4.73317 13.571 4.55317 13.571 4.33317V3.39984L12.6377 3.4065C12.4177 3.4065 12.2377 3.21984 12.2377 2.99984C12.2377 2.77984 12.4177 2.59984 12.6377 2.59984L13.2244 2.6065H13.571V1.67317C13.571 1.45317 13.751 1.2665 13.971 1.2665C14.191 1.2665 14.371 1.45317 14.371 1.67317L14.3644 2.1465V2.59984H15.3044C15.5244 2.59984 15.7044 2.77984 15.7044 2.99984C15.6977 3.11317 15.651 3.2065 15.5777 3.2865Z" fill="#BDBDBD" />
                    <path d="M6.97046 6.91992C7.84675 6.91992 8.55712 6.20954 8.55712 5.33325C8.55712 4.45696 7.84675 3.74658 6.97046 3.74658C6.09416 3.74658 5.38379 4.45696 5.38379 5.33325C5.38379 6.20954 6.09416 6.91992 6.97046 6.91992Z" fill="#BDBDBD" />
                    <path d="M14.9504 5.3335H14.637V8.40683L14.5504 8.3335C14.0304 7.88683 13.1904 7.88683 12.6704 8.3335L9.89704 10.7135C9.37704 11.1602 8.53704 11.1602 8.01704 10.7135L7.79038 10.5268C7.31704 10.1135 6.56371 10.0735 6.03038 10.4335L3.53704 12.1068C3.39038 11.7335 3.30371 11.3002 3.30371 10.7935V5.20683C3.30371 3.32683 4.29704 2.3335 6.17704 2.3335H11.637V2.02016C11.637 1.7535 11.6837 1.52683 11.7904 1.3335H6.17704C3.75038 1.3335 2.30371 2.78016 2.30371 5.20683V10.7935C2.30371 11.5202 2.43038 12.1535 2.67704 12.6868C3.25038 13.9535 4.47704 14.6668 6.17704 14.6668H11.7637C14.1904 14.6668 15.637 13.2202 15.637 10.7935V5.18016C15.4437 5.28683 15.217 5.3335 14.9504 5.3335Z" fill="#BDBDBD" />
                  </svg>

                }
              />
            }
            body={
              <Flex direction={'column'} gap={8} p={10} w={'280px'} justify={'center'}>
                <Flex gap={8}>
                  <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M21.149 1.3125H18.209C17.339 1.3125 16.699 1.6725 16.409 2.3125C16.249 2.6025 16.179 2.9425 16.179 3.3425V6.2825C16.179 7.5525 16.939 8.3125 18.209 8.3125H21.149C21.549 8.3125 21.889 8.2425 22.179 8.0825C22.819 7.7925 23.179 7.1525 23.179 6.2825V3.3425C23.179 2.0725 22.419 1.3125 21.149 1.3125ZM22.089 5.2425C21.989 5.3425 21.839 5.4125 21.679 5.4225H20.269V5.9325L20.279 6.8125C20.269 6.9825 20.209 7.1225 20.089 7.2425C19.989 7.3425 19.839 7.4125 19.679 7.4125C19.349 7.4125 19.079 7.1425 19.079 6.8125V5.4125L17.679 5.4225C17.349 5.4225 17.079 5.1425 17.079 4.8125C17.079 4.4825 17.349 4.2125 17.679 4.2125L18.559 4.2225H19.079V2.8225C19.079 2.4925 19.349 2.2125 19.679 2.2125C20.009 2.2125 20.279 2.4925 20.279 2.8225L20.269 3.5325V4.2125H21.679C22.009 4.2125 22.279 4.4825 22.279 4.8125C22.269 4.9825 22.199 5.1225 22.089 5.2425Z" fill="#BDBDBD" />
                    <path d="M9.17895 10.6926C10.4934 10.6926 11.559 9.62706 11.559 8.31262C11.559 6.99818 10.4934 5.93262 9.17895 5.93262C7.86451 5.93262 6.79895 6.99818 6.79895 8.31262C6.79895 9.62706 7.86451 10.6926 9.17895 10.6926Z" fill="#BDBDBD" />
                    <path d="M21.149 8.3125H20.679V12.9225L20.549 12.8125C19.769 12.1425 18.509 12.1425 17.729 12.8125L13.569 16.3825C12.789 17.0525 11.529 17.0525 10.749 16.3825L10.409 16.1025C9.69895 15.4825 8.56896 15.4225 7.76896 15.9625L4.02895 18.4725C3.80895 17.9125 3.67896 17.2625 3.67896 16.5025V8.1225C3.67896 5.3025 5.16896 3.8125 7.98896 3.8125H16.179V3.3425C16.179 2.9425 16.249 2.6025 16.409 2.3125H7.98896C4.34895 2.3125 2.17896 4.4825 2.17896 8.1225V16.5025C2.17896 17.5925 2.36896 18.5425 2.73896 19.3425C3.59896 21.2425 5.43896 22.3125 7.98896 22.3125H16.369C20.009 22.3125 22.179 20.1425 22.179 16.5025V8.0825C21.889 8.2425 21.549 8.3125 21.149 8.3125Z" fill="#BDBDBD" />
                  </svg>
                  <Text color="white" size={'sm'} fw={700}>
                    Reference image
                  </Text>
                </Flex>
                {spaceStore.influnceImage ? (
                  <Flex direction="column" gap={8}>
                    <Flex justify="space-between" align="center">
                      <Text fz={14}>Influence image</Text>
                      <Button
                        variant="subtle"
                        color="red"
                        size="xs"
                        onClick={() => spaceStore.setInfluenceImage({ src: null })}
                      >
                        Remove
                      </Button>
                    </Flex>
                    <ImageObserved
                      src={spaceStore.influnceImage}
                      width="100%"
                      height="auto"
                      imgStyle={{ borderRadius: '6px', overflow: 'hidden' }}
                    />
                  </Flex>
                ) : <Flex
                  w="100%"
                  justify="center"
                  align="center"
                  className={classes.uploadContainer}
                >
                  <Widget
                    imagesOnly
                    localeTranslations={{
                      buttons: {
                        choose: {
                          images: {
                            one: 'Upload object'
                          },
                        },
                      },
                    }}
                    clearable={false}
                    // @ts-ignore
                    onChange={async (info: {
                      crop?: {
                        width: number
                        height: number
                      },
                      cdnUrl: string,
                      originalImageInfo: {
                        width: number
                        height: number
                      }
                    }) => {
                      const res = await runAsyncConvert({ url: info.cdnUrl, removeBackground: false })
                      const prompt = await runAsyncVision({ url: res.url })
                      spaceStore.setPrompt(prompt?.prompt)
                      spaceStore.setInfluenceImage({ src: res.url })
                    }}
                    tabs={"file camera url"}
                    publicKey="35626f59762b63c4244c"
                    previewStep={false}
                    styles={{
                      root: classes.widgetRoot
                    }}
                  />
                </Flex>}

              </Flex>
            }
          />
          <BottomButton
            data-intercom-target={
              'GetPromptFromImageButton'
            }
            loading={runAsyncVisionLoading}
            label="Get prompt from image"
            onClick={async () => {
              const visionRes = await runAsyncVision({ url: spaceStore.selectedModel?.mainImage || '' })
              spaceStore.setPrompt(visionRes?.prompt)
            }}
            icon={<svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.1589 0.666626H12.1989C11.6189 0.666626 11.1922 0.906626 10.9989 1.33329C10.8922 1.52663 10.8456 1.75329 10.8456 2.01996V3.97996C10.8456 4.82663 11.3522 5.33329 12.1989 5.33329H14.1589C14.4256 5.33329 14.6522 5.28663 14.8456 5.17996C15.2722 4.98663 15.5122 4.55996 15.5122 3.97996V2.01996C15.5122 1.17329 15.0056 0.666626 14.1589 0.666626ZM14.7856 3.28663C14.7189 3.35329 14.6189 3.39996 14.5122 3.40663H13.5722V3.74663L13.5789 4.33329C13.5722 4.44663 13.5322 4.53996 13.4522 4.61996C13.3856 4.68663 13.2856 4.73329 13.1789 4.73329C12.9589 4.73329 12.7789 4.55329 12.7789 4.33329V3.39996L11.8456 3.40663C11.6256 3.40663 11.4456 3.21996 11.4456 2.99996C11.4456 2.77996 11.6256 2.59996 11.8456 2.59996L12.4322 2.60663H12.7789V1.67329C12.7789 1.45329 12.9589 1.26663 13.1789 1.26663C13.3989 1.26663 13.5789 1.45329 13.5789 1.67329L13.5722 2.14663V2.59996H14.5122C14.7322 2.59996 14.9122 2.77996 14.9122 2.99996C14.9056 3.11329 14.8589 3.20663 14.7856 3.28663Z" fill="#BDBDBD" />
              <path d="M6.17895 6.92004C7.05524 6.92004 7.76562 6.20966 7.76562 5.33337C7.76562 4.45708 7.05524 3.7467 6.17895 3.7467C5.30266 3.7467 4.59229 4.45708 4.59229 5.33337C4.59229 6.20966 5.30266 6.92004 6.17895 6.92004Z" fill="#BDBDBD" />
              <path d="M14.159 5.33337H13.8457V8.40671L13.759 8.33337C13.239 7.88671 12.399 7.88671 11.879 8.33337L9.10566 10.7134C8.58566 11.16 7.74566 11.16 7.22566 10.7134L6.999 10.5267C6.52566 10.1134 5.77233 10.0734 5.239 10.4334L2.74566 12.1067C2.599 11.7334 2.51233 11.3 2.51233 10.7934V5.20671C2.51233 3.32671 3.50566 2.33337 5.38566 2.33337H10.8457V2.02004C10.8457 1.75337 10.8923 1.52671 10.999 1.33337H5.38566C2.959 1.33337 1.51233 2.78004 1.51233 5.20671V10.7934C1.51233 11.52 1.639 12.1534 1.88566 12.6867C2.459 13.9534 3.68566 14.6667 5.38566 14.6667H10.9723C13.399 14.6667 14.8457 13.22 14.8457 10.7934V5.18004C14.6523 5.28671 14.4257 5.33337 14.159 5.33337Z" fill="#BDBDBD" />
            </svg>}
          />

          <SidebarTrigger
            target={
              <BottomButton
                loading={false}
                dataIntercomTarget={
                  'AspectRatioButton'
                }
                label={spaceStore.ratio}
                onClick={async () => {
                }}
                icon={
                  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10.9723 1.64551L5.38566 1.64551C2.959 1.64551 1.51233 3.09217 1.51233 5.51884L1.51233 11.0988C1.51233 13.5322 2.959 14.9788 5.38566 14.9788L10.9657 14.9788C13.3923 14.9788 14.839 13.5322 14.839 11.1055L14.839 5.51884C14.8457 3.09217 13.399 1.64551 10.9723 1.64551ZM12.679 6.97884C12.679 7.25217 12.4523 7.47884 12.179 7.47884C11.9057 7.47884 11.679 7.25217 11.679 6.97884V5.51884L5.38566 11.8122H6.84566C7.119 11.8122 7.34566 12.0388 7.34566 12.3122C7.34566 12.5855 7.119 12.8122 6.84566 12.8122H4.179C4.11233 12.8122 4.04566 12.7988 3.98566 12.7722C3.86566 12.7188 3.76566 12.6255 3.71233 12.4988C3.69233 12.4388 3.679 12.3788 3.679 12.3122L3.679 9.64551C3.679 9.37217 3.90566 9.14551 4.179 9.14551C4.45233 9.14551 4.679 9.37217 4.679 9.64551V11.1055L10.9723 4.81217H9.51233C9.239 4.81217 9.01233 4.58551 9.01233 4.31217C9.01233 4.03884 9.239 3.81217 9.51233 3.81217L12.179 3.81217C12.2457 3.81217 12.3057 3.82551 12.3723 3.85217C12.4923 3.90551 12.5923 3.99884 12.6457 4.12551C12.6657 4.18551 12.679 4.24551 12.679 4.31217V6.97884Z" fill="#BDBDBD" />
                  </svg>
                }
              />
            }
            body={
              <Flex direction={'column'} gap={8} p={10} w={'280px'} justify={'center'}>
                <Text fz={16}>Choose aspect ratio</Text>
                <Text fz={12}>Add your reference image to improve the existed prompt</Text>
                <Flex w={'100%'} gap={4} wrap={'wrap'}>
                  {
                   [
                    '1:1',
                    // '16:9',
                    // '21:9',
                    '2:3',
                    '3:2',
                    '4:5',
                    '5:4',
                    // '9:16',
                    // '9:21'
                  ].map((side) => {
                      console.log(spaceStore.ratio, side)
                      return (
                        <Button
                          size="xs"
                          style={{
                            width: '80px',
                            border: `1px solid ${spaceStore.ratio === side ? '#000000' : '#BDBDBD'}`,
                            color: spaceStore.ratio === side ? '#000000' : '#BDBDBD',
                            fontSize: '14px',
                            backgroundColor: spaceStore.ratio === side ? '#FFFFFF' : 'transparent',
                          }}
                          variant="outline"
                          onClick={() => {
                            spaceStore.setRatio(side)
                          }}
                        >{side}</Button>
                      )
                    })
                  }
                </Flex>
              </Flex>
            }
          />

          <SidebarTrigger
            target={
              <BottomButton
                loading={false}
                label={'Advanced settings'}
                icon={<svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.9389 5.14663C13.1322 5.64663 13.6122 5.99996 14.1789 5.99996C14.9122 5.99996 15.5122 5.39996 15.5122 4.66663C15.5122 3.93329 14.9122 3.33329 14.1789 3.33329C13.6122 3.33329 13.1322 3.68663 12.9389 4.18663C12.9056 4.17996 12.8789 4.16663 12.8456 4.16663H10.1789V3.66663C10.1789 3.11996 9.72558 2.66663 9.17891 2.66663L7.17891 2.66663C6.63225 2.66663 6.17891 3.11996 6.17891 3.66663V4.16663L3.51225 4.16663C3.47891 4.16663 3.45225 4.17996 3.41891 4.18663C3.22558 3.68663 2.74558 3.33329 2.17891 3.33329C1.44558 3.33329 0.845581 3.93329 0.845581 4.66663C0.845581 5.39996 1.44558 5.99996 2.17891 5.99996C2.74558 5.99996 3.22558 5.64663 3.41891 5.14663C3.45225 5.15329 3.47891 5.16663 3.51225 5.16663L5.22558 5.16663C3.85891 6.17996 3.01225 7.85996 3.01225 9.66663C3.01225 9.77996 3.01891 9.88663 3.03225 9.99996H2.84558C2.29225 9.99996 1.84558 10.4466 1.84558 11L1.84558 12.3333C1.84558 12.8866 2.29225 13.3333 2.84558 13.3333H4.17891C4.32558 13.3333 4.45891 13.3 4.58558 13.24C4.93225 13.0933 5.17891 12.74 5.17891 12.3333V11C5.17891 10.4466 4.73225 9.99996 4.17891 9.99996H4.02558C4.02558 9.97996 4.03225 9.95996 4.03225 9.93996C4.01891 9.84663 4.01891 9.75996 4.01891 9.66663C4.01891 8.01996 4.86558 6.51329 6.19225 5.73329C6.21891 6.24663 6.65225 6.66663 7.17891 6.66663L9.17891 6.66663C9.70558 6.66663 10.1389 6.24663 10.1722 5.73329C11.4989 6.51329 12.3456 8.02663 12.3456 9.66663C12.3456 9.75996 12.3389 9.84663 12.3322 9.93996C12.3322 9.95996 12.3389 9.97996 12.3389 9.99996H12.1789C11.6256 9.99996 11.1789 10.4466 11.1789 11V12.3333C11.1789 12.74 11.4256 13.0933 11.7722 13.24C11.8989 13.3 12.0322 13.3333 12.1789 13.3333H13.5122C14.0656 13.3333 14.5122 12.8866 14.5122 12.3333V11C14.5122 10.4466 14.0656 9.99996 13.5122 9.99996H13.3256C13.3389 9.88663 13.3456 9.77996 13.3456 9.66663C13.3456 7.85996 12.4989 6.17996 11.1322 5.16663H12.8456C12.8789 5.16663 12.9056 5.15329 12.9389 5.14663Z" fill="#BDBDBD" />
                </svg>}
              />
            }
            body={
              <Flex direction={'column'} gap={8} p={10} w={'280px'} justify={'center'}>
                <Flex direction={'column'} gap={12}>
                  {
                    (spaceStore.secondLoraName || spaceStore.moodLora) &&
                    <>
                      <Text
                        fz={14}
                        mt={12}
                        size="xs"
                      >
                        Guidance Scale
                      </Text>
                      <Slider
                        size="sm"
                        color="orange"
                        sx={{ maxWidth: 360 }}
                        mt={0}
                        min={1}
                        max={4}
                        step={0.1}
                        label={spaceStore.cfg}
                        value={+spaceStore.cfg}
                        onChangeEnd={(e) => {
                          spaceStore.setCfg(+e.toFixed(2))
                        }}
                      />
                    </>
                  }

                  <Text
                    size="xs"
                    fz={14}
                    mt={12}
                  >
                    Generation steps
                  </Text>
                  <Slider
                    size="sm"
                    color="orange"
                    sx={{ maxWidth: 360 }}
                    mt={0}
                    min={20}
                    max={50}
                    step={1}
                    label={spaceStore.steps}
                    value={+spaceStore.steps}
                    onChangeEnd={(e) => {
                      spaceStore.setSteps(+e.toFixed(2))
                    }}
                  />
                  <Text
                    fz={14}
                    mt={12}
                    size="xs"
                    data-intercom-target={
                      'trainStrength'
                    }>
                    Location straight
                  </Text>
                  <Slider
                    size="sm"
                    color="orange"
                    sx={{ maxWidth: 360 }}
                    mt={0}
                    min={0}
                    max={2}
                    step={0.1}
                    label={spaceStore.locationScale}
                    value={+spaceStore.locationScale}
                    onChangeEnd={(e) => {
                      spaceStore.setLocationScale(+e.toFixed(2))
                    }}
                  />
                  <Text fz={12}>Controls AI creativity. Lower values make the AI more creative, using the model less. Higher values make it follow the model more closely, with less variation. Best results between 0.5 and 1.4</Text>

                  {
                    spaceStore.secondLoraName &&
                    <Text
                      size="xs"
                      fz={14}
                      mt={12}
                      data-intercom-target={
                        'trainStrength'
                      }>
                      Car straight
                    </Text>
                  }
                  {
                    spaceStore.moodLora &&
                    <Text
                      size="xs"
                      fz={14}
                      mt={12}
                      data-intercom-target={
                        'trainStrength'
                      }>
                      Mood straight
                    </Text>
                  }
                  {
                    (spaceStore.secondLoraName || spaceStore.moodLora) &&
                    <Slider
                      size="sm"
                      color="orange"
                      sx={{ maxWidth: 360 }}
                      mt={0}
                      min={0}
                      max={2}
                      step={0.1}
                      label={spaceStore.secondLoraScale}
                      value={+spaceStore.secondLoraScale}
                      onChangeEnd={(e) => {
                        spaceStore.setSecondLoraScale(+e.toFixed(2))
                      }}
                    />}

                  {
                    spaceStore.secondLoraName &&
                    <Text fz={12}>Controls The Custom Car LORA creativity.</Text>
                  }
                </Flex>
              </Flex>
            }
          />

        </Flex>
      </PromptField>
    </Flex>
  );
};

export const Variations = observer(Element);